import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification } from '@lizrd/contracts';
import {
  dismissNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../lib/api';
import { relativeTime } from '../lib/graph-visuals';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Polling keeps the count in sync across sessions (INAPP-008).
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    refetchInterval: 30_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const readOne = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });
  const dismiss = useMutation({ mutationFn: dismissNotification, onSuccess: invalidate });

  const unread = q.data?.unread ?? 0;
  const items = q.data?.notifications ?? [];

  function openItem(n: Notification) {
    if (!n.read) readOne.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative grid h-8 w-8 place-items-center rounded-sm border border-border text-haze transition-colors hover:border-border-strong hover:bg-surface-2 hover:text-bone"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-md border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
              <span className="text-sm font-semibold">Notifications</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => readAll.mutate()}
                  className="text-xs text-iris hover:text-iris-strong"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 divide-y divide-hairline overflow-y-auto">
              {items.length === 0 && (
                <p className="p-4 text-sm text-muted">You're all caught up.</p>
              )}
              {items.map((n) => (
                <div
                  key={n.id}
                  className={`flex items-start gap-2 p-3 ${n.read ? '' : 'bg-surface-2'}`}
                >
                  <span
                    className={`mt-1.5 h-1.5 w-1.5 flex-none rounded-full ${
                      n.read ? 'bg-transparent' : 'bg-iris'
                    }`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={() => openItem(n)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate text-sm font-medium text-bone">{n.title}</p>
                    <p className="text-xs text-muted">{n.body}</p>
                    <p className="mt-0.5 text-[0.7rem] text-muted">{relativeTime(n.createdAt)}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => dismiss.mutate(n.id)}
                    aria-label="Dismiss"
                    className="flex-none text-muted hover:text-bone"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="border-t border-hairline px-3 py-1.5 text-center text-[0.7rem] text-muted">
                Showing unread and the last 7 days
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
